import React, { useState, useEffect, useRef } from 'react'
import { Link, useLocation, useSearchParams } from 'react-router-dom'
import { api, downloadUrl, downloadBulkZip } from '../api/client'
import { useToast } from '../context/useToast'
import PageShell from '../components/ui/PageShell'
import Card from '../components/ui/Card'
import Badge, { getGenreBadgeVariant } from '../components/ui/Badge'
import StatCard from '../components/ui/StatCard'
import SectionTitle from '../components/ui/SectionTitle'
import { PrimaryButton, SecondaryButton, DangerButton } from '../components/ui/Button'
import ProgressBar from '../components/ProgressBar'

function estimatedSecondsRemaining(job) {
  if (!job || (job.status !== 'pending' && job.status !== 'running')) return null
  if (job.progress == null || job.progress <= 0 || job.progress >= 100) return null
  const started = job.started_at ? new Date(job.started_at).getTime() : null
  if (!started || Number.isNaN(started)) return null
  const elapsedSec = (Date.now() - started) / 1000
  return Math.max(0, Math.round((elapsedSec * (100 - job.progress)) / job.progress))
}

function formatRemaining(seconds) {
  if (seconds == null) return null
  if (seconds < 60) return `ca. ${Math.round(seconds)} Sek.`
  return `ca. ${Math.round(seconds / 60)} Min.`
}

const STEP_LABELS = {
  preflight: 'Preflight',
  decode: 'Decode',
  analyze: 'Analyse',
  plan: 'Plan',
  ai_cleanup: 'AI Cleanup',
  render: 'Render',
  limit_safety: 'Limit/Safety',
  qc: 'Qualitätsprüfung',
  tagging: 'Tagging',
  analyzing: 'Analyse',
  mastering: 'Plan',
  rendering: 'Render',
  finalizing: 'Abschluss',
  cover: 'Cover wird erstellt',
  canvas: 'Canvas wird erstellt',
}

const FETCH_TIMEOUT_MS = 90000
const CHUNK_SIZE = 10

export default function Dashboard() {
  const toast = useToast()
  const location = useLocation()
  const [searchParams] = useSearchParams()
  const highlightTrackIdRef = useRef(null)
  const [credits, setCredits] = useState(null)
  const [data, setData] = useState({ tracks: [], jobs: [], tracksTotalFromHeader: null })
  const [loading, setLoading] = useState(true)
  const [deletingId, setDeletingId] = useState(null)
  const [batchSelected, setBatchSelected] = useState({})
  const [batchArtist, setBatchArtist] = useState('')
  const [batchAlbum, setBatchAlbum] = useState('')
  const [batchGenre, setBatchGenre] = useState('')
  const [batchYear, setBatchYear] = useState('')
  const [batchAutoClean, setBatchAutoClean] = useState(false)
  const [batchApplying, setBatchApplying] = useState(false)
  const [bulkDownloading, setBulkDownloading] = useState(false)

  const tracks = data.tracks
  const jobs = data.jobs
  const tracksTotalFromHeader = data.tracksTotalFromHeader
  const tracksMismatch = tracksTotalFromHeader != null && !Number.isNaN(tracksTotalFromHeader) && tracksTotalFromHeader > tracks.length
  const displayTracks = Array.isArray(tracks) ? tracks : []

  const fetchIdRef = React.useRef(0)

  const fetchData = React.useCallback(() => {
    const id = ++fetchIdRef.current
    const timeoutId = setTimeout(() => {
      setLoading(false)
    }, FETCH_TIMEOUT_MS)

    const fetchJobs = () => api.get('/jobs').then((r) => (Array.isArray(r?.data?.jobs) ? r.data.jobs : [])).catch(() => [])

    const fetchTracksChunked = (totalCount) => {
      if (!totalCount || totalCount <= 0) return Promise.resolve([])
      const offsets = []
      for (let o = 0; o < totalCount; o += CHUNK_SIZE) offsets.push(o)
      return Promise.all(offsets.map((offset) => api.get(`/tracks?offset=${offset}&limit=${CHUNK_SIZE}`).then((r) => (Array.isArray(r?.data) ? r.data : [])).catch(() => [])))
        .then((chunks) => {
          const flat = chunks.flat()
          const byId = new Map()
          flat.forEach((t) => byId.set(t.id, t))
          return Array.from(byId.values()).sort((a, b) => (b.created_at || '').localeCompare(a.created_at || ''))
        })
    }

    Promise.all([
      api.get('/tracks/count').then((r) => (r?.data?.count != null ? Number(r.data.count) : null)).catch(() => null),
      fetchJobs(),
    ])
      .then(([count, j]) => {
        if (id !== fetchIdRef.current) return
        return fetchTracksChunked(count ?? 0).then((tracks) => {
          if (id !== fetchIdRef.current) return
          setData({ tracks: Array.isArray(tracks) ? tracks : [], jobs: Array.isArray(j) ? j : [], tracksTotalFromHeader: count })
        })
      })
      .catch(() => {
        if (id !== fetchIdRef.current) return
        setData({ tracks: [], jobs: [], tracksTotalFromHeader: null })
      })
      .finally(() => {
        clearTimeout(timeoutId)
        if (id === fetchIdRef.current) setLoading(false)
      })
  }, [])

  const fetchDataRef = useRef(fetchData)
  fetchDataRef.current = fetchData

  useEffect(() => {
    api.get('/me/credits').then((r) => setCredits(r.data)).catch(() => setCredits(null))
  }, [])
  useEffect(() => {
    fetchDataRef.current()
  }, [])

  // Nach Upload: highlightTrackId oder ?track= → Refetch damit neuer Track erscheint, dann ggf. scrollen
  const highlightTrackId = location.state?.highlightTrackId ?? (searchParams.get('track') ? Number(searchParams.get('track')) : null)
  useEffect(() => {
    if (highlightTrackId == null || Number.isNaN(highlightTrackId)) return
    fetchDataRef.current()
    const t1 = setTimeout(() => { fetchDataRef.current() }, 1500)
    const t2 = setTimeout(() => { fetchDataRef.current() }, 2000)
    return () => { clearTimeout(t1); clearTimeout(t2) }
  }, [highlightTrackId])

  // Scroll zu neuem Track sobald Liste geladen ist
  useEffect(() => {
    if (highlightTrackId == null || loading || !displayTracks.length) return
    const el = highlightTrackIdRef.current
    if (el) {
      requestAnimationFrame(() => { el.scrollIntoView({ behavior: 'smooth', block: 'nearest' }) })
    }
  }, [highlightTrackId, loading, displayTracks.length])

  // Refetch when tab becomes visible so list is up to date after upload in another tab
  useEffect(() => {
    const onVisible = () => { if (document.visibilityState === 'visible') fetchDataRef.current() }
    window.addEventListener('visibilitychange', onVisible)
    return () => window.removeEventListener('visibilitychange', onVisible)
  }, [])

  // While jobs are running: poll every 4s; otherwise poll every 30s so list stays up to date
  useEffect(() => {
    const hasRunning = jobs.some((j) => j.status === 'pending' || j.status === 'running')
    const intervalMs = hasRunning ? 4000 : 30000
    const interval = setInterval(() => fetchDataRef.current(), intervalMs)
    return () => clearInterval(interval)
  }, [jobs])

  const deleteTrack = async (e, trackId) => {
    e.preventDefault()
    e.stopPropagation()
    if (!window.confirm('Track wirklich löschen? Alle zugehörigen Daten werden entfernt.')) return
    setDeletingId(trackId)
    try {
      await api.delete(`/tracks/${trackId}`)
      setData((prev) => ({ ...prev, tracks: prev.tracks.filter((t) => t.id !== trackId), jobs: prev.jobs.filter((j) => j.track_id !== trackId) }))
      toast('Track gelöscht', 'success')
    } catch (err) {
      toast(err.message || 'Löschen fehlgeschlagen', 'error')
    } finally {
      setDeletingId(null)
    }
  }

  const completedCount = displayTracks.filter((t) => t.status === 'completed').length
  const failedCount = displayTracks.filter((t) => t.status === 'failed').length
  const runningJobs = jobs.filter((j) => j.status === 'pending' || j.status === 'running').length
  const jobByTrackId = React.useMemo(() => {
    const m = {}
    jobs.forEach((j) => {
      if (j.track_id) m[j.track_id] = j
    })
    return m
  }, [jobs])

  const batchApply = async () => {
    const ids = Object.keys(batchSelected).filter((id) => batchSelected[id])
    if (ids.length === 0) {
      toast('Keine Tracks ausgewählt', 'error')
      return
    }
    setBatchApplying(true)
    try {
      const body = { track_ids: ids.map(Number) }
      if (batchArtist.trim()) body.artist = batchArtist.trim()
      if (batchAlbum.trim()) body.album = batchAlbum.trim()
      if (batchGenre.trim()) body.genre = batchGenre.trim()
      const y = parseInt(batchYear, 10)
      if (!Number.isNaN(y)) body.year = y
      if (batchAutoClean) body.auto_clean = true
      await api.post('/tracks/batch/metadata', body)
      toast(`${ids.length} Track(s) aktualisiert`, 'success')
      setBatchSelected({})
      fetchData()
    } catch (e) {
      toast(e.response?.data?.detail || e.message || 'Batch fehlgeschlagen', 'error')
    } finally {
      setBatchApplying(false)
    }
  }

  const toggleBatchSelect = (id) => {
    setBatchSelected((prev) => ({ ...prev, [id]: !prev[id] }))
  }

  const bulkSelectedIds = Object.keys(batchSelected).filter((id) => batchSelected[id]).map(Number)
  const bulkWavIds = bulkSelectedIds.filter((id) => {
    const t = tracks.find((tr) => tr.id === id)
    return t?.status === 'completed' && t?.has_wav_download
  })
  const bulkMp3Ids = bulkSelectedIds.filter((id) => {
    const t = tracks.find((tr) => tr.id === id)
    return t?.status === 'completed' && t?.has_mp3_download
  })
  const doBulkDownload = async (format) => {
    const ids = format === 'wav' ? bulkWavIds : bulkMp3Ids
    if (!ids.length) {
      toast(format === 'wav' ? 'Keine ausgewählten fertigen Tracks mit WAV' : 'Keine ausgewählten fertigen Tracks mit MP3', 'error')
      return
    }
    setBulkDownloading(true)
    try {
      await downloadBulkZip(ids, format, `masters_${format}.zip`)
      toast(`${ids.length} Track(s) als ZIP gestartet`, 'success')
    } catch (e) {
      toast(e.message || 'Massendownload fehlgeschlagen', 'error')
    } finally {
      setBulkDownloading(false)
    }
  }

  const isInProgress = (track) => {
    if (track.status === 'uploaded' || track.status === 'analyzing' || track.status === 'mastering') return true
    const j = jobByTrackId[track.id]
    return !!(j && (j.status === 'pending' || j.status === 'running'))
  }
  const jobProgress = (track) => {
    const j = jobByTrackId[track.id]
    if (!j || (j.status !== 'pending' && j.status !== 'running')) return null
    return j.progress != null ? j.progress : null
  }

  return (
    <PageShell>
      <section className="space-y-6" aria-label="Meine Masters">
        <SectionTitle className="mb-4">Meine Masters{displayTracks.length > 0 || loading ? ` (${loading ? '…' : displayTracks.length})` : ''}</SectionTitle>

        {credits != null && (
          <Card className="flex flex-row items-center justify-between gap-4 p-4 flex-wrap">
            <span className="text-white/80">Credits</span>
            {credits.unlimited ? (
              <span className="font-semibold text-violet-300">Unlimited</span>
            ) : (
              <span className="font-semibold text-white">{credits.balance}</span>
            )}
            <div className="flex gap-2">
              <Link to="/dashboard/buy-credits">
                <SecondaryButton>Credits bestellen</SecondaryButton>
              </Link>
              <Link to="/dashboard/upload">
                <PrimaryButton>Neues Mastering</PrimaryButton>
              </Link>
            </div>
          </Card>
        )}

        <div className="text-center py-2">
          <p className="mx-auto max-w-xl text-white/70">
            Studio-Qualität in Minuten: KI analysiert deinen Track und erstellt ein Mastering für alle Plattformen.
          </p>
          <div className="mt-4 flex flex-wrap justify-center gap-3">
            <Link to="/dashboard/upload">
              <PrimaryButton>Track hochladen & masteren</PrimaryButton>
            </Link>
            <Link to="/dashboard/jobs">
              <SecondaryButton>Job-Queue</SecondaryButton>
            </Link>
          </div>
        </div>

        {tracksMismatch && (
          <div className="rounded-lg border border-amber-500/50 bg-amber-500/10 px-4 py-3 text-sm text-amber-200">
            Der Server meldet <strong>{tracksTotalFromHeader} Tracks</strong>, angekommen sind <strong>{displayTracks.length}</strong>. Bitte mit <kbd className="rounded bg-white/20 px-1">Strg+Shift+R</kbd> neu laden.
          </div>
        )}

        {!loading && (displayTracks.length > 0 || jobs.length > 0) && (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4" key={`stats-${displayTracks.length}`}>
            <StatCard label="Tracks gesamt" value={displayTracks.length} />
            <StatCard label="Fertig gemastert" value={displayTracks.filter((t) => t.status === 'completed').length} hint="Ready" />
            <StatCard label="In Bearbeitung" value={runningJobs} hint="Running" />
            <StatCard label="Fehlgeschlagen" value={displayTracks.filter((t) => t.status === 'failed').length} hint="Failed" />
          </div>
        )}

        {!loading && displayTracks.length > 1 && (
          <Card>
            <SectionTitle className="mb-4">Batch-Metadaten</SectionTitle>
            <p className="mb-4 text-sm text-white/70">Tracks unten auswählen und gemeinsame Metadaten setzen oder Titel auto-clean ausführen.</p>
            <div className="mb-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <div>
                <label className="mb-1 block text-xs text-white/50">Künstler</label>
                <input type="text" value={batchArtist} onChange={(e) => setBatchArtist(e.target.value)} className="w-full rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-white placeholder-white/30" placeholder="Künstler" />
              </div>
              <div>
                <label className="mb-1 block text-xs text-white/50">Album</label>
                <input type="text" value={batchAlbum} onChange={(e) => setBatchAlbum(e.target.value)} className="w-full rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-white placeholder-white/30" placeholder="Album" />
              </div>
              <div>
                <label className="mb-1 block text-xs text-white/50">Genre</label>
                <input type="text" value={batchGenre} onChange={(e) => setBatchGenre(e.target.value)} className="w-full rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-white placeholder-white/30" placeholder="Genre" />
              </div>
              <div>
                <label className="mb-1 block text-xs text-white/50">Jahr</label>
                <input type="number" min="1900" max="2100" value={batchYear} onChange={(e) => setBatchYear(e.target.value)} className="w-full rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-white placeholder-white/30" placeholder="Jahr" />
              </div>
            </div>
            <div className="mb-4 flex flex-wrap items-center gap-4">
              <label className="flex cursor-pointer items-center gap-2 text-sm text-white/90">
                <input type="checkbox" checked={batchAutoClean} onChange={(e) => setBatchAutoClean(e.target.checked)} className="rounded" />
                Titel auto-clean
              </label>
              <PrimaryButton onClick={batchApply} disabled={batchApplying || !Object.values(batchSelected).some(Boolean)}>
                {batchApplying ? 'Wird angewendet…' : `Auf ${Object.values(batchSelected).filter(Boolean).length} Track(s) anwenden`}
              </PrimaryButton>
              <SecondaryButton onClick={() => doBulkDownload('wav')} disabled={bulkDownloading || bulkWavIds.length === 0}>
                {bulkDownloading ? '…' : `Massendownload WAV${bulkWavIds.length ? ` (${bulkWavIds.length})` : ''}`}
              </SecondaryButton>
              <SecondaryButton onClick={() => doBulkDownload('mp3')} disabled={bulkDownloading || bulkMp3Ids.length === 0}>
                {bulkDownloading ? '…' : `Massendownload MP3${bulkMp3Ids.length ? ` (${bulkMp3Ids.length})` : ''}`}
              </SecondaryButton>
            </div>
          </Card>
        )}

        {loading ? (
            <div className="flex items-center justify-center gap-3 py-12 text-white/60">
              <span className="h-5 w-5 animate-spin rounded-full border-2 border-violet-500 border-t-transparent" aria-hidden />
              <span>Tracks werden geladen…</span>
            </div>
          ) : displayTracks.length === 0 ? (
            <Card className="flex flex-col items-center justify-center gap-4 py-16 text-center">
              <p className="max-w-md text-white/70">
                Noch keine Masters. Lade deinen ersten Track hoch – die KI übernimmt Analyse und Mastering.
              </p>
              <div className="flex flex-wrap items-center justify-center gap-3">
                <Link to="/dashboard/upload">
                  <PrimaryButton>Track hochladen</PrimaryButton>
                </Link>
                <SecondaryButton onClick={() => { setLoading(true); fetchData(); }}>
                  Erneut laden
                </SecondaryButton>
              </div>
            </Card>
          ) : (
            <div className="max-h-[70vh] overflow-y-auto rounded-lg border border-white/10 pr-1" aria-label={`${displayTracks.length} Tracks – Liste durchscrollen`}>
              <ul className="space-y-3">
              {displayTracks.map((t, idx) => {
                const inProgress = isInProgress(t)
                const job = jobByTrackId[t.id]
                const progress = jobProgress(t)
                const remainingSec = job ? estimatedSecondsRemaining(job) : null
                const stepLabel = job?.step ? STEP_LABELS[job.step] || job.step : null
                const hasCover = t.has_cover_download === true
                const statusLabel = t.status === 'completed' ? 'Fertig' : t.status === 'failed' ? 'Fehler' : t.status === 'analyzing' ? 'Analyse' : t.status === 'mastering' ? 'Mastering' : t.status === 'uploaded' ? 'In Warteschlange' : t.status
                return (
                  <li key={`track-${t.id}`} ref={t.id === highlightTrackId ? highlightTrackIdRef : undefined}>
                    <Card className="overflow-hidden transition hover:border-white/20 focus-within:ring-0 focus-within:outline-none">
                      {displayTracks.length > 1 && (
                        <div className="flex items-center gap-2 border-b border-white/10 px-4 py-2">
                          <input type="checkbox" checked={!!batchSelected[t.id]} onChange={() => toggleBatchSelect(t.id)} className="rounded focus:ring-0 focus:ring-offset-0 focus:outline-none" onClick={(e) => e.stopPropagation()} />
                          <span className="text-xs text-white/50">Für Batch auswählen</span>
                        </div>
                      )}
                      <Link to={`/dashboard/tracks/${t.id}`} className="block outline-none ring-0 focus:outline-none focus:ring-0">
                        <div className="flex flex-wrap items-center gap-3 px-4 py-3 sm:gap-4">
                          {hasCover && (
                            <div className="h-14 w-14 shrink-0 overflow-hidden rounded-lg bg-white/5">
                              <img src={downloadUrl(t.id, 'cover')} alt="" className="h-full w-full object-cover" loading="lazy" decoding="async" onError={(e) => { e.target.style.display = 'none' }} />
                            </div>
                          )}
                          <div className="min-w-0 flex-1">
                            <h3 className="truncate font-semibold text-white">{t.title_display || t.original_filename}</h3>
                            <div className="mt-1 flex flex-wrap items-center gap-2">
                              <Badge variant={t.status}>{statusLabel}</Badge>
                              {t.genre && <Badge variant={getGenreBadgeVariant(t.genre)}>{t.genre}</Badge>}
                              {t.duration_seconds != null && <span className="text-xs text-white/50">{Math.round(t.duration_seconds)}s</span>}
                            </div>
                            {inProgress && (
                              <div className="mt-2 flex flex-wrap items-center gap-2">
                                <span className="h-3 w-3 shrink-0 animate-spin rounded-full border-2 border-violet-500 border-t-transparent" aria-hidden />
                                <span className="text-sm text-white/70">{stepLabel ? `${stepLabel} …` : 'Wird gestartet…'}</span>
                                {remainingSec != null && <span className="text-sm font-medium text-violet-300">Noch {formatRemaining(remainingSec)}</span>}
                                {progress == null && !stepLabel && remainingSec == null && <span className="text-xs text-white/50">Fortschritt wird ermittelt…</span>}
                              </div>
                            )}
                            {inProgress && (
                              <div className="mt-1.5 max-w-xs">
                                <ProgressBar progress={progress ?? 0} showLabel barClassName="transition-none" />
                              </div>
                            )}
                          </div>
                          <div className="flex shrink-0 flex-wrap items-center gap-2">
                            <Link to={`/dashboard/tracks/${t.id}`} onClick={(e) => e.stopPropagation()}>
                              <PrimaryButton>Öffnen</PrimaryButton>
                            </Link>
                            {t.status === 'completed' && (
                              <a href={downloadUrl(t.id, 'wav')} download rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}>
                                <SecondaryButton>Download WAV</SecondaryButton>
                              </a>
                            )}
                            <DangerButton disabled={deletingId === t.id} onClick={(e) => deleteTrack(e, t.id)}>
                              {deletingId === t.id ? '…' : 'Löschen'}
                            </DangerButton>
                          </div>
                        </div>
                      </Link>
                    </Card>
                  </li>
                )
              })}
            </ul>
            </div>
          )}
      </section>
    </PageShell>
  )
}
